import React, { ReactNode } from 'react';
import { getDaysText } from 'utils/translations/i18n';
import { useI18nFormatting } from 'hooks/use-i18n-formatting';
import ClockIcon from 'components/icon/clock-icon';
import MoneyHouseIcon from 'components/icon/money-house-icon';
import OverAskingIcon from 'components/icon/over-asking-icon';
import UnderAskingIcon from 'components/icon/under-asking-icon';
import styles from './style.module.scss';
import { TryScrambleText } from 'utils/text-scrambler';
import { listingPageModalIds } from 'constants/test-constants';
import Button from 'components/control/button';
import type { Zoopraisal } from 'data/zoopraisal';
import { SellingRange } from 'data/zoopraisal/types';
import { TFunction, Trans, useTranslation } from 'next-i18next';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';


//#region types
type PropertyAppraisalProps = {
  price: number;
  daysOnMarket: number | null;
  tenantName: string;
  agentDisclaimerLabel: string;
  isPrivate: boolean;
  shouldScramble: boolean;
  appraisal: Readonly<Zoopraisal>;
  sellingRange: SellingRange;
  hideAvm?: boolean;
  renderTitle?: () => ReactNode;
  onContactAnAgentClick: () => void;
};
//#endregion

//#region sub-components
function ExpectedToSellPriceRange({ start, end, isPrivate, shouldScramble }: { start: number; end: number; isPrivate: boolean; shouldScramble: boolean }) {
  const { t } = useTranslation(Namespaces.COMMON);
  const { formatCurrency } = useI18nFormatting();

  const startText = TryScrambleText(formatCurrency(start), shouldScramble);
  const endText = TryScrambleText(formatCurrency(end), shouldScramble);

  if (start > 0 && end > 0) {
    return (
      <section className={styles['stat-row']}>
        <Trans
          t={t}
          i18nKey={LOCALE_COMMON_PATHS.ADDRESS_HOME_APPRAISAL_ESTIMATED_SELL_RANGE}
          values={ { start: startText, end: endText }}
          components={{ bold: <b className={isPrivate ? 'blur' : ''} /> }}
        />
      </section>
    );
  }
  return null;
}

function SimilarPropertiesAverage({ averagePrice, isPrivate, shouldScramble }: { averagePrice: number; isPrivate: boolean; shouldScramble: boolean }) {
  const { t } = useTranslation(Namespaces.COMMON);
  const { formatCurrency } = useI18nFormatting();
  const averagePriceText = TryScrambleText(formatCurrency(averagePrice), shouldScramble);
  return (
    <section className={styles['stat-row']}>
      <span className={styles['grey-icon']}>
        <MoneyHouseIcon />
      </span>
      <Trans t={t} i18nKey={LOCALE_COMMON_PATHS.ADDRESS_HOME_APPRAISAL_SIMILAR_PROPERTIES_AVERAGE} values={{ averagePrice: averagePriceText }} components={{ bold: <b className={isPrivate ? 'blur' : ''} /> }} />
    </section>
  );
}

function ExpectedToPay({ percentageSimilarSold, isOverAsking }: { percentageSimilarSold: number; isOverAsking: boolean }) {
  const { t } = useTranslation(Namespaces.COMMON);
  const context = isOverAsking ? undefined : 'under';
  return (
    <section className={styles['stat-row']}>
      <span className={styles['grey-icon']}>
        {isOverAsking ? <OverAskingIcon /> : <UnderAskingIcon />}
      </span>
      <Trans
        t={t}
        i18nKey={LOCALE_COMMON_PATHS.ADDRESS_HOME_APPRAISAL_EXPECT_TO_PAY}
        values={{ percentageSimilarSold }}
        context={context}
        components={{ bold: <b className={isOverAsking ? 'blur' : ''} /> }}
      />
    </section>
  );
}

function ExpectedToSellIn({ estimatedSoldDateInFuture, estimatedTimeOnMarketRemaining, averageDaysOnMarket, daysOnMarket, isPrivate, shouldScramble, tenantName }: { estimatedTimeOnMarketRemaining: string; averageDaysOnMarket: string; daysOnMarket: string; isPrivate: boolean; shouldScramble: boolean; estimatedSoldDateInFuture: boolean; tenantName: string }) {
  const { t } = useTranslation(Namespaces.COMMON);

  const estimatedTimeOnMarketRemainingText = TryScrambleText(estimatedTimeOnMarketRemaining, shouldScramble);
  const averageDaysOnMarketText = TryScrambleText(averageDaysOnMarket, shouldScramble);
  const daysOnMarketText = TryScrambleText(daysOnMarket, shouldScramble);

  return (
    <section className={styles['stat-row']}>
      <span className={styles['grey-icon']}>
        <ClockIcon />
      </span>
      <Trans
        t={t}
        i18nKey={LOCALE_COMMON_PATHS.ADDRESS_HOME_APPRAISAL_EXPECTED_TO_SELL}
        values={
          { estimatedTimeOnMarketRemaining: estimatedTimeOnMarketRemainingText,
            averageDaysOnMarket: averageDaysOnMarketText,
            daysOnMarket: daysOnMarketText,
            tenantName,
          }
        }
        components={{ bold: <b className={isPrivate ? 'blur' : ''} /> }}
        context={estimatedSoldDateInFuture ? 'future' : undefined}
      />
    </section>
  );
}

function EstimationDisclaimer({ agentText, onContactAnAgentClick }: { agentText: string; onContactAnAgentClick: () => void }) {
  const { t } = useTranslation(Namespaces.COMMON);
  return (
    <section className={styles.disclaimer}>
      <p className={styles['estimation-disclaimer-text']}>
        {t(LOCALE_COMMON_PATHS.ADDRESS_HOME_APPRAISAL_ESTIMATION_DISCLAIMER, { agentText })}
      </p>
      <Button className={styles['contact-button']} label={t(LOCALE_COMMON_PATHS.ADDRESS_HOME_APPRAISAL_CONTACT_AGENT_BUTTON)} onClick={onContactAnAgentClick} />
    </section>
  );
}

function PoweredByZoocasa() {
  const { t } = useTranslation(Namespaces.COMMON);
  return (
    <section className={styles['zoocasa-link']}>
      <Trans
        t={t}
        i18nKey={LOCALE_COMMON_PATHS.ADDRESS_HOME_APPRAISAL_POWERED_BY_ZOOCASA}
        components={{ hyperlink: <a href="https://www.zoocasa.com" target='_blank' rel="noreferrer" /> }}
      />
    </section>
  );
}
//#endregion

//#region main component
export default function PropertyAppraisal({ tenantName, agentDisclaimerLabel, renderTitle, price, daysOnMarket, isPrivate, shouldScramble, appraisal, sellingRange, hideAvm, onContactAnAgentClick }: PropertyAppraisalProps) {
  const { t } = useTranslation(Namespaces.COMMON);
  const { averagePrice, overAskingCount, datasetCount, averageDaysOnMarket } = appraisal;
  const showMarketTime = daysOnMarket != null;
  const daysOnMarketText = showMarketTime ? getDaysText(daysOnMarket, t) : null;
  const averageDaysOnMarketText = showMarketTime ? getDaysText(averageDaysOnMarket, t) : null;
  const estimatedTimeOnMarketRemaining = showMarketTime ? getEstimatedTimeRemainingOnMarket(averageDaysOnMarket, daysOnMarket, t) : null;
  const estimatedSoldDateInFuture = showMarketTime && (averageDaysOnMarket || 0) - (daysOnMarket || 0) > 0;
  const percentageSimilarSold = Math.round(overAskingCount / datasetCount * 100); const isOverAsking = Number(price) - (averagePrice as number) < 0;
  const showExpectedToPay = percentageSimilarSold / 100 > 0.6;
  const { start, end } = sellingRange;

  return (
    <div className={styles.component} data-testid={listingPageModalIds.zoopraisal}>
      { renderTitle ? renderTitle() : <h2>{t(LOCALE_COMMON_PATHS.ADDRESS_HOME_APPRAISAL_TITLE)}</h2>}
      <div>
        {!hideAvm && <ExpectedToSellPriceRange start={start} end={end} isPrivate={isPrivate} shouldScramble={shouldScramble} />}
        <SimilarPropertiesAverage averagePrice={averagePrice} isPrivate={isPrivate} shouldScramble={shouldScramble} />
        { showExpectedToPay &&
          <ExpectedToPay percentageSimilarSold={percentageSimilarSold} isOverAsking={isOverAsking} />
        }
        {showMarketTime && (
          <ExpectedToSellIn
            estimatedSoldDateInFuture={estimatedSoldDateInFuture}
            estimatedTimeOnMarketRemaining={estimatedTimeOnMarketRemaining}
            averageDaysOnMarket={averageDaysOnMarketText}
            daysOnMarket={daysOnMarketText}
            isPrivate={isPrivate}
            shouldScramble={shouldScramble} tenantName={tenantName} />
        )}
        <EstimationDisclaimer agentText={agentDisclaimerLabel} onContactAnAgentClick={onContactAnAgentClick} />
        <PoweredByZoocasa />
      </div>
    </div>
  );
}
//#endregion

function getEstimatedTimeRemainingOnMarket(averageDaysOnMarket: number, daysOnMarket: number, t: TFunction): string | null {
  const timeRemaining = (averageDaysOnMarket || 0) - (daysOnMarket || 0);
  const context = timeRemaining > 0 ? undefined : 'very_soon';
  const timeRemainingVerbiage = t(LOCALE_COMMON_PATHS.ADDRESS_HOME_APPRAISAL_ESTIMATED_TIME_REMAINING_ON_MARKET,
    {
      count: timeRemaining,
      defaultValue: '{{count}} days.',
      context,
    }
  );
  return timeRemainingVerbiage;
}

